import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ErrorApi } from '../servicios/api.js';
import { Aviso, Cargando } from '../componentes/piezas.js';
import { EscanerQr } from '../componentes/camara.js';
import { NuevaSolicitud } from './solicitudes.js';

interface ResolucionPublica {
  entidad: 'UBICACION' | 'ACTIVO' | 'PARTE';
  entidadId: string;
  nombre: string;
  detalle: string | null;
  ubicacionId: string | null;
  organizacion: string;
  tenantSlug: string;
}

/**
 * Solicitud sin sesión, desde el QR de un equipo (`/s` y `/s/:codigo`).
 *
 * Sin código, la página es el punto de entrada: el usuario escanea la etiqueta
 * del equipo con la cámara del navegador. Con código, muestra el formulario de
 * siempre, con la ubicación y el activo que ya fijó el escaneo.
 */
export function SolicitudPublica(): JSX.Element {
  const { codigo } = useParams();
  // La organización viene en el enlace del QR (`/s/<código>?t=<organización>`).
  const [parametros] = useSearchParams();
  const tenant = parametros.get('t');
  const navegar = useNavigate();
  const cliente = useQueryClient();
  const [escaneando, setEscaneando] = useState(false);

  return (
    // Mismo contenedor con scroll que el área de contenido de la app: el formulario queda
    // idéntico al interno y su botón de envío siempre se alcanza, también en celular.
    <main className="solicitud-publica-raiz">

      {codigo ? (
        <SolicitudDeCodigo codigo={codigo} tenant={tenant} alEscanear={() => setEscaneando(true)} />
      ) : (
        <div className="panel">
          <div className="panel-cuerpo">
            <button type="button" className="boton" onClick={() => setEscaneando(true)}>
              Escanear QR del equipo
            </button>
          </div>
        </div>
      )}

      {escaneando ? (
        <EscanerQr
          alDetectar={(detectado) => {
            setEscaneando(false);
            // El código escaneado es de la misma organización que la sede de la página: se
            // conserva `?t=` para que no vuelva a buscarse en todas las organizaciones.
            // Si el enlace abierto no trae `?t=`, la organización es la que devolvió la sede ya cargada.
            const sede = codigo ? cliente.getQueryData<ResolucionPublica>(['solicitud-publica', codigo, tenant ?? '']) : undefined;
            const organizacionActual = tenant ?? sede?.tenantSlug ?? null;
            navegar(`/s/${encodeURIComponent(detectado)}${organizacionActual ? `?t=${encodeURIComponent(organizacionActual)}` : ''}`);
          }}
          alCerrar={() => setEscaneando(false)}
        />
      ) : null}
    </main>
  );
}

function SolicitudDeCodigo({
  codigo,
  tenant,
  alEscanear,
}: {
  codigo: string;
  tenant: string | null;
  alEscanear: () => void;
}): JSX.Element {
  const [creada, setCreada] = useState<number | null>(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ['solicitud-publica', codigo, tenant ?? ''],
    queryFn: () =>
      api.get<ResolucionPublica>(
        `/publico/qr/${encodeURIComponent(codigo)}${tenant ? `?t=${encodeURIComponent(tenant)}` : ''}`,
      ),
    retry: false,
  });

  if (isLoading) return <Cargando texto="Buscando el equipo…" />;

  if (error || !data) {
    const mensaje = error instanceof ErrorApi ? error.message : 'No se pudo leer el código.';
    return (
      <>
        <Aviso tono="atencion">{mensaje}</Aviso>
        <div className="panel" style={{ marginTop: 16 }}>
          <div className="panel-cuerpo">
            <button type="button" className="boton" onClick={alEscanear}>
              Escanear otro código
            </button>
          </div>
        </div>
      </>
    );
  }

  if (creada !== null) {
    return (
      <div className="panel">
        <div className="panel-cuerpo">
          <h2>Solicitud #{creada} registrada</h2>
          <p>El equipo de mantenimiento la revisará. Gracias por reportarla.</p>
          <button type="button" className="boton secundario" onClick={alEscanear}>
            Reportar otra falla
          </button>
        </div>
      </div>
    );
  }

  // Sin tarjeta previa: el formulario arranca directo con la ubicación de la sede escaneada.
  return (
    <>
      <NuevaSolicitud
        publico={{
          codigo,
          // Sin `?t=` (etiquetas impresas antes del cambio), se usa la organización que devolvió la sede.
          tenant: tenant ?? data.tenantSlug,
          inicial: {
            ubicacionId: data.ubicacionId,
            activoId: data.entidad === 'ACTIVO' ? data.entidadId : null,
          },
          alCrear: setCreada,
        }}
      />
    </>
  );
}
